import { useCallback } from 'react'
import { useUrlState } from './useUrlState'

export type LiveControlStatus = 'all' | 'cancel-held' | 'live' | 'upcoming-soon' | 'upcoming' | 'ended' | 'suspended' | 'problem'

export interface LiveControlFilters {
  status: LiveControlStatus
  playerIds: string[]
  publishingStatuses: string[]
  competitionIds: string[]
  courtIds: string[]
  categories: string[]
  search: string
  page: number
}

/**
 * Hook for managing Live & Upcoming Matches filters in URL
 * Provides type-safe filter state and updater, matching the pattern of
 * useMatchesFilters and useVodFilters for feature parity.
 */
export function useLiveControlFilters() {
  const defaultFilters: LiveControlFilters = {
    status: 'all',
    playerIds: [],
    publishingStatuses: ['PUBLISHED'],
    competitionIds: [],
    courtIds: [],
    categories: [],
    search: '',
    page: 1,
  }

  const { state, updateState } = useUrlState<LiveControlFilters>(
    defaultFilters,
    {
      // Status tab - only include in URL if not 'all'
      status: {
        serialize: (value) => (value !== 'all' ? value : undefined),
        deserialize: (value) => (value as LiveControlStatus) || 'all',
      },
      // Player IDs - comma-separated
      playerIds: {
        serialize: (value) => (value && value.length > 0 ? value.join(',') : undefined),
        deserialize: (value) => (value ? value.split(',').filter(Boolean) : []),
      },
      // Publishing statuses - comma-separated; omit when just ['PUBLISHED'] (the default)
      publishingStatuses: {
        serialize: (value) => {
          if (!value || value.length === 0) return 'none'
          // Omit from URL when it matches the default
          if (value.length === 1 && value[0] === 'PUBLISHED') return undefined
          return value.join(',')
        },
        deserialize: (value) => {
          if (value === null) return ['PUBLISHED'] // No param = default
          if (value === 'none') return []
          return value.split(',').filter(Boolean)
        },
      },
      // Competition IDs - comma-separated
      competitionIds: {
        serialize: (value) => (value && value.length > 0 ? value.join(',') : undefined),
        deserialize: (value) => (value ? value.split(',').filter(Boolean) : []),
      },
      // Court/venue IDs - comma-separated
      courtIds: {
        serialize: (value) => (value && value.length > 0 ? value.join(',') : undefined),
        deserialize: (value) => (value ? value.split(',').filter(Boolean) : []),
      },
      // Categories - comma-separated
      categories: {
        serialize: (value) => (value && value.length > 0 ? value.join(',') : undefined),
        deserialize: (value) => (value ? value.split(',').filter(Boolean) : []),
      },
      // Search - only include in URL if not empty
      search: {
        serialize: (value) => (value && value.trim() ? value.trim() : undefined),
        deserialize: (value) => (value || ''),
      },
      // Page - only include in URL if > 1
      page: {
        serialize: (value) => (value > 1 ? String(value) : undefined),
        deserialize: (value) => (value ? parseInt(value, 10) : 1),
      },
    }
  )

  /** Update filters and reset to page 1 */
  const updateFilterAndResetPage = useCallback(
    (updates: Partial<LiveControlFilters>) => {
      updateState({ ...updates, page: 1 })
    },
    [updateState]
  )

  /** Change page and scroll to top */
  const handlePageChange = useCallback(
    (newPage: number) => {
      updateState({ page: newPage })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    [updateState]
  )

  return {
    filters: state,
    updateFilters: updateState,
    updateFilterAndResetPage,
    handlePageChange,
  }
}
